/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Text from '../../../shared/components/Text/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import useRefetchRecurringData from '../components/presentational/hooks/useRefetchRecurringData';
import {
  deleteSavedCalendarItem,
  deleteSavedWorkoutItem,
  setDeleteStack,
  deleteSavedWorkoutItemsForWeek,
  removePresaveProgramItemFromRedux,
  removePresaveItemFromRedux,
  duplicateWeek,
  fetchNonDateBasedProgramItemsByDayRange,
  deleteRecurringItem,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  margin-top: 16px;
  padding: 12px;
  background: #FFF3CD;
  border: 1px solid #FFE69C;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const SubHeaderText = styled('div')`
  margin-bottom: 24px;
  text-align: left;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeleteButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }
`;

const DuplicateButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: #2C2C2C;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: white;
  color: #3C3C3C;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #F5F5F5;
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;

const LinkText = styled('a')`
  color: #007AFF;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ConfirmDeleteModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const refetchRecurringData = useRefetchRecurringData();
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const deleteStack = useSelector((state) => state.calendar.data.deleteStack);
  const programType = useSelector((state) => state.calendar.data.programType);
  const calendarItems = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems) : useSelector((state) => state.calendar.data.calendarItems);
  const dateToUse = modalData?.date || selectedDate;
  const formattedDate = moment(dateToUse).format('YYYY-MM-DD');
  const itemCount = ((modalData?.bulkDeleteAssignedIds?.length || 0)
  + (modalData?.preSaveItems?.length || 0)) || 1;
  const itemText = itemCount === 1 ? 'workout item' : 'workout items';
  const isRecurring = modalData?.isRecurring;

  // eslint-disable-next-line no-unused-vars
  const [deleteOption, setDeleteOption] = useState('single');

  const currentMonth = moment(selectedDate).startOf('month').toDate();
  const nextMonth = moment(selectedDate).add(1, 'month').startOf('month').toDate();

  const handleRemovePresaveItemFromRedux = (assigned) => {
    if (programType === 'free-form-program') {
      dispatch(removePresaveProgramItemFromRedux(modalData?.dayNumber, null, assigned));
    } else {
      dispatch(removePresaveItemFromRedux(modalData?.date, null, assigned));
    }
  };

  const handleDelete = () => {
    if (modalData) {
      if (modalData?.preSaveItems?.length > 0) {
        /** Handle preSave items immediately via Redux removal */
        modalData?.preSaveItems.forEach((item) => handleRemovePresaveItemFromRedux(item.assignedId));
      }
      /** Get the actual content to add to the delete stack */
      const content = calendarItems[formattedDate]?.exercises[modalData?.itemIndex];
      if (content) {
        /** Add to delete stack */
        const newDeleteStack = [...deleteStack];
        newDeleteStack.push(content);
        dispatch(setDeleteStack(newDeleteStack));
      }

      /** we need to pass the id as an array for the endpoint because it also accomodates a bulk
       * delete it needed
       */
      const assignedIds = modalData?.isBulkDelete ? modalData?.bulkDeleteAssignedIds : [modalData?.assignedId];

      /** Extract exercise names for toast messages */
      let exerciseNames = [];
      if (programType === 'free-form-program') {
        const exercises = calendarItems[modalData?.dayNumber]?.exercises || [];
        exerciseNames = assignedIds
          .map((id) => exercises.find((ex) => ex.assignedId === id))
          .filter(Boolean)
          .map((ex) => ex.title || ex.exercise?.description || ex.description || 'Exercise');
      } else {
        const exercises = calendarItems[formattedDate]?.exercises || [];
        exerciseNames = assignedIds
          .map((id) => exercises.find((ex) => ex.assignedId === id))
          .filter(Boolean)
          .map((ex) => ex.title || ex.exercise?.description || ex.description || 'Exercise');
      }

      /** Delete the item */
      if (programType === 'free-form-program' && assignedIds?.length > 0) {
        if (modalData?.deleteWeek) {
          dispatch(deleteSavedWorkoutItemsForWeek(
            currentUser.accountCode,
            assignedIds,
            modalData?.weekNumber,
          ));
        } else {
          dispatch(deleteSavedWorkoutItem(
            currentUser.accountCode,
            assignedIds,
            modalData?.itemIndex,
            modalData?.dayNumber,
            exerciseNames,
          ));
        }
      }
      if (programType === 'calendar' && assignedIds?.length > 0) {
        if (isRecurring) {
          const [assignedId] = assignedIds ?? [];
          if (!assignedId) return;
          /** when we delete a recurring item schedule, onsuccess we need to
           * fetchCalendarPickerItems so that calendar dots update
           * we also need to re fetch recurring items so that we can get
           * the proper updates in redux. These updates for recurring are too
           * complex to try and update directly in state, so fethcing again is the
           * safest option
           */
          dispatch(deleteRecurringItem(
            currentUser.accountCode,
            assignedId,
            formattedDate,
            isRecurring,
            {
              onSuccess: () => {
                refetchRecurringData({
                  currentUser,
                  currentMonth,
                  nextMonth,
                  selectedReduxCalendar,
                  selectedReduxAthlete,
                  closeModal,
                });
              },
              onError: () => {},
            },
          ));
        } else {
          dispatch(deleteSavedCalendarItem(
            currentUser.accountCode,
            assignedIds,
            modalData?.itemIndex,
            formattedDate,
            isRecurring && deleteOption === 'all',
            exerciseNames,
          ));
        }
      }
    }
    closeModal();
  };

  const handleDuplicate = () => {
    dispatch(duplicateWeek(accountCode, modalData?.savedWorkoutId, modalData?.weekNumber, {
      onSuccess: () => {
        const refresh = true;
        dispatch(fetchNonDateBasedProgramItemsByDayRange(
          accountCode,
          modalData?.savedWorkoutId,
          null,
          null,
          refresh,
          modalData?.weekNumber,
        ));
      },
      onError: () => {},
    }));
  };

  return (
    <ModalContainer>
      <CloseButton onClick={closeModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color='grey'
        />
      </CloseButton>

      {modalData?.duplicateWeek ? (
        <IconCircle>
          <IcomoonReact
            iconSet={iconSet}
            size={32}
            icon='warning'
          />
        </IconCircle>
      ) : (
        <IconCircle>
          <IcomoonReact
            iconSet={iconSet}
            size={32}
            icon='trashcan'
            color='#DB3030'
          />
        </IconCircle>
      )}

      <ModalText>
        {modalData?.programName && (
          <>
            <Text
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
              fontSize='20px'
              fontWeight={600}
            >
              {modalData?.programName}
            </Text>
            {modalData?.dayInWeek ? (
              <Text fontSize='18px'>{`Week ${modalData?.weekNumber} - Day ${modalData?.dayInWeek}`}</Text>
            ) : (
              <Text fontSize='18px'>{`Week ${modalData?.weekNumber}`}</Text>
            )}
          </>
        )}
        {modalData?.duplicateWeek ? (
          <Text fontSize='20px' fontWeight={600}>
            {`You are about to duplicate Week  ${modalData?.weekNumber}`}
          </Text>
        ) : (
          <Text fontSize='20px' fontWeight={600}>
            {isRecurring ? 'Delete Recurring Instances' : `You are about to delete (${itemCount}) ${itemText}`}
          </Text>
        )}
      </ModalText>

      {isRecurring && (
        <SubHeaderText>
          <Text fontSize='14px' color='#666666'>
            Are you sure you want to delete? This action will delete all instances of this recurring item.
            {' '}
          </Text>
          <LinkText
            href='https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items'
            target='_blank'
          >
            Click here to learn more.
          </LinkText>
        </SubHeaderText>
      )}
      {modalData?.isParentCalendarItem && (
        <WarningText style={{ marginBottom: 16 }}>
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon='warning'
            color='#856404'
            style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
          />
          <Text fontSize='14px' color='#856404'>
            Warning: you are trying to delete an item that belongs to a parent calendar - this action will only affect the item on the child calendar
          </Text>
        </WarningText>
      )}

      {modalData?.duplicateWeek && (
        <WarningText>
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon='warning'
            color='#856404'
            style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
          />
          <Text fontSize='14px' color='#856404'>
            Warning: duplicating a week will refresh the program and cause any pre save items to be removed. Make sure all items are saved before duplicating.
          </Text>
        </WarningText>
      )}

      {modalData?.deleteWeek && programType === 'free-form-program' && (
      <WarningText style={{ marginBottom: 8 }}>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='warning'
          color='#856404'
          style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
        />
        <Text fontSize='14px' color='#856404'>
          Warning: clearing a week will refresh the program. This will cause any unsaved items to be removed from the program and may also cause empty trailing weeks to be removed from the program.
        </Text>
      </WarningText>
      )}

      <ButtonsContainer>
        {modalData?.duplicateWeek ? (
          <DuplicateButton onClick={handleDuplicate}>
            DUPLICATE
          </DuplicateButton>
        ) : (
          <DeleteButton onClick={handleDelete}>
            DELETE
          </DeleteButton>
        )}
        <CancelButton onClick={closeModal}>
          CANCEL
        </CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmDeleteModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ConfirmDeleteModal;
